import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PRODUCTS } from '../../data/products';

const serviceKeys = ['diagnose', 'build', 'ai', 'train'] as const;

const ProductsOfferings: React.FC<{ id?: string; asPage?: boolean }> = ({ id = 'products', asPage = false }) => {
  const { t } = useTranslation();

  return (
    <section id={id} className="scroll-mt-20 px-4 py-20 sm:px-6" aria-labelledby={`${id}-title`}>
      <div className="mx-auto max-w-[1180px]">
        <div className="mb-10 max-w-3xl">
          <p className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.14em] text-muted">
            <span className="rounded-md border border-amber/40 px-2 py-0.5 font-mono text-amber">03</span>
            {t('products.eyebrow')}
          </p>
          {asPage ? (
            <h1 id={`${id}-title`} className="font-display text-4xl font-medium tracking-tight text-paper sm:text-5xl">
              {t('products.title')}
            </h1>
          ) : (
            <h2 id={`${id}-title`} className="font-display text-4xl font-medium tracking-tight text-paper sm:text-5xl">
              {t('products.title')}
            </h2>
          )}
          <p className="mt-4 text-lg text-muted">{t('products.lede')}</p>
        </div>

        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="rounded-full border border-amber/40 bg-amber-dim px-2.5 py-1 font-mono text-[0.68rem] uppercase tracking-wide text-amber">
              {t('products.badgeProducts')}
            </span>
            <h3 className="mt-3 font-display text-2xl text-paper">{t('products.productsHeading')}</h3>
          </div>
          <p className="max-w-md text-sm text-muted">{t('products.productsNote')}</p>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {PRODUCTS.map((product) => (
            <article key={product.slug} className="flex flex-col rounded-2xl border border-amber/25 bg-graphite p-5">
              <span className="font-mono text-[0.68rem] uppercase tracking-wide text-amber">{t(`prod.${product.key}.tag`)}</span>
              <h3 className="mt-3 font-display text-2xl text-paper">{t(`prod.${product.key}.name`)}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted">{t(`prod.${product.key}.does`)}</p>
              <p className="mt-4 border-t border-line pt-4 text-sm leading-relaxed text-paper/90">
                <span className="mb-1 block font-mono text-[0.68rem] uppercase tracking-wide text-coral">
                  {t('products.problemLabel')}
                </span>
                {t(`prod.${product.key}.problem`)}
              </p>
              <Link
                to={`/products/${product.slug}`}
                className="mt-5 inline-flex text-sm font-semibold text-amber hover:text-mint"
              >
                {t('products.view')} →
              </Link>
            </article>
          ))}
        </div>

        <div className="mt-14 rounded-[28px] border border-mint/25 bg-[#0d1614] p-5 sm:p-7">
          <span className="rounded-full border border-mint/40 bg-mint-dim px-2.5 py-1 font-mono text-[0.68rem] uppercase tracking-wide text-mint">
            {t('products.badgeServices')}
          </span>
          <h3 className="mt-3 font-display text-2xl text-paper">{t('products.servicesHeading')}</h3>
          <p className="mt-2 max-w-3xl text-sm text-muted">{t('products.servicesNote')}</p>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {serviceKeys.map((key) => (
              <article key={key} className="rounded-2xl border border-mint/25 bg-ink/60 p-5">
                <span className="font-mono text-[0.68rem] uppercase tracking-wide text-mint">{t(`svc.${key}.tag`)}</span>
                <h3 className="mt-3 font-display text-xl text-paper">{t(`svc.${key}.name`)}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted">{t(`svc.${key}.does`)}</p>
                <p className="mt-4 border-t border-line pt-4 text-sm text-paper/90">
                  <span className="mb-1 block font-mono text-[0.68rem] uppercase tracking-wide text-stamp">
                    {t('products.outcomeLabel')}
                  </span>
                  {t(`svc.${key}.outcome`)}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsOfferings;
